import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import type { LoyaltyRewardsConfig } from 'loyalty/hooks/useLoyaltyRewardsNotification'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'

const MobileNotificationCard = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(8)};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  padding: ${rem(12)};
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  pointer-events: auto;
`

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;
`

const CloseButtonWrapper = styled.div`
  flex-shrink: 0;
  align-self: flex-start;
`

interface Props {
  config: LoyaltyRewardsConfig
  onViewMembership?: () => void
  onClose?: () => void
}

function LoyaltyRewardsNotificationMobile({
  config,
  onViewMembership,
  onClose,
}: Props) {
  const intl = useIntl()

  const closeAriaLabel = intl.formatMessage({
    id: 'ieK2AuGngPh3brS04pBa',
    defaultMessage: 'Close rewards notification',
    description: 'Aria label for close button on societe rewards mobile notification',
  })

  const viewMembershipLabel = intl.formatMessage({
    id: 'xWfSjxHhIzdnI4m-2ZeU',
    defaultMessage: 'View membership',
    description: 'Link text to view loyalty membership from mobile rewards notification',
  })

  const pointsDelta = config.currentPoints - config.previousPoints
  const creditsDelta = config.currentStatusCredits - config.previousStatusCredits
  const nightsDelta = config.currentNights - config.previousNights
  const hasPositiveDelta = pointsDelta > 0 || creditsDelta > 0 || nightsDelta > 0
  const isPlatinumRetainCelebration = !!config.isPlatinumRetainCelebration && !config.isUpgrade
  const tierIconTier = config.isUpgrade && config.newTier ? config.newTier : config.tier

  // Pre-compute pluralised unit labels so combined messages stay single-plural (lint rule)
  const creditsText = intl.formatMessage(
    {
      id: '1iz4BJx-mk_XVxfYw7uT',
      defaultMessage: '+{n} Status {n, plural, one {Credit} other {Credits}}',
      description: 'Loyalty credits earned label used in mobile rewards notification delta summary',
    },
    { n: creditsDelta },
  )
  const nightsText = intl.formatMessage(
    {
      id: '9e2S2l4i85kJdSbsgW0Z',
      defaultMessage: '+{n, plural, one {# night} other {# nights}}',
      description: 'Loyalty nights earned label used in mobile rewards notification delta summary',
    },
    { n: nightsDelta },
  )

  const pointsCreditsNightsSummary = intl.formatMessage(
    {
      id: 'NMe3A4rK65eIrQv63awW',
      defaultMessage: '+{points} pts · {credits} · {nights}',
      description: 'Mobile rewards notification summary line when points, status credits, and nights all increased',
    },
    { points: pointsDelta, credits: creditsText, nights: nightsText },
  )

  const creditsAndNightsSummary = intl.formatMessage(
    {
      id: 'FFgCT_K4MKjvIFZaVOgd',
      defaultMessage: '{credits} · {nights}',
      description: 'Mobile rewards notification summary when only status credits and nights increased',
    },
    { credits: creditsText, nights: nightsText },
  )

  const defaultRewardsHeadline = (
    <FormattedMessage
      id="xSeAHYmfZBxGCdNtEZwt"
      defaultMessage="Your rewards have been added"
      description="Heading text displayed in the mobile Société rewards notification card"
    />
  )

  let mobileHeadline: React.ReactNode
  if (config.isUpgrade && config.newTier) {
    mobileHeadline = (
      <BodyText variant="medium" weight="bold">
        <FormattedMessage
          id="FH-MjeObCYhBtAw8q1r2"
          defaultMessage="You've reached {tierName}"
          description="Mobile Société rewards heading when the member unlocks a higher tier"
          values={{ tierName: getLoyaltyTierDisplayName(config.newTier) }}
        />
      </BodyText>
    )
  } else if (isPlatinumRetainCelebration) {
    mobileHeadline = (
      <>
        <BodyText variant="medium" weight="bold">
          <FormattedMessage
            id="r21ABumNzE7qAfsct5WR"
            defaultMessage="Congratulations you have retained {tierName} for another year."
            description="Banner congratulating user on retaining their loyalty tier for another year"
            values={{ tierName: getLoyaltyTierDisplayName('platinum') }}
          />
        </BodyText>
        {config.expiryDate && (
          <BodyText variant="small" colour="light-foreground-alt">
            <FormattedMessage
              id="tVYmPHkbjS-OE7jNuhpK"
              defaultMessage="Review date {date}"
              description="Loyalty tier review date display in drawer congratulations banner"
              values={{ date: <FormatDateTime value={config.expiryDate} format="dmy-casual-short" /> }}
            />
          </BodyText>
        )}
      </>
    )
  } else {
    mobileHeadline = (
      <BodyText variant="medium" weight="bold">
        {defaultRewardsHeadline}
      </BodyText>
    )
  }

  return (
    <MobileNotificationCard>
      <LoyaltyTierIcon tier={tierIconTier} size={40} />

      <ContentSection>
        {mobileHeadline}

        <BodyText
          variant="small"
          colour={hasPositiveDelta ? 'success' : 'light-foreground-alt'}
          weight={hasPositiveDelta ? 'bold' : 'normal'}
        >
          {pointsDelta > 0 && creditsDelta > 0 && nightsDelta > 0 && pointsCreditsNightsSummary}
          {pointsDelta > 0 && creditsDelta > 0 && nightsDelta === 0 && (
            <FormattedMessage
              id="DP0fBdZBk_Jhahtzajer"
              defaultMessage="+{points} pts · +{credits} Status {credits, plural, one {Credit} other {Credits}}"
              description="Mobile rewards notification showing points and credits deltas"
              values={{ points: pointsDelta, credits: creditsDelta }}
            />
          )}
          {pointsDelta > 0 && creditsDelta === 0 && nightsDelta > 0 && (
            <FormattedMessage
              id="X30gkdnXjqEwoRaY7DD9"
              defaultMessage="+{points} pts · +{nights, plural, one {# night} other {# nights}}"
              description="Mobile rewards notification showing points and nights deltas"
              values={{ points: pointsDelta, nights: nightsDelta }}
            />
          )}
          {pointsDelta > 0 && creditsDelta === 0 && nightsDelta === 0 && (
            <FormattedMessage
              id="GvKRFmOFFaB4i_Y3qYlT"
              defaultMessage="+{points} pts added"
              description="Mobile rewards notification showing points delta only"
              values={{ points: pointsDelta }}
            />
          )}
          {pointsDelta === 0 && creditsDelta > 0 && nightsDelta > 0 && creditsAndNightsSummary}
          {pointsDelta === 0 && creditsDelta > 0 && nightsDelta === 0 && (
            <FormattedMessage
              id="FY7ClTUL98wQJSBQP0H_"
              defaultMessage="+{credits} Status {credits, plural, one {Credit} other {Credits}} added"
              description="Mobile rewards notification showing credits delta only"
              values={{ credits: creditsDelta }}
            />
          )}
          {pointsDelta === 0 && creditsDelta === 0 && nightsDelta > 0 && (
            <FormattedMessage
              id="mcCn_WRJhqISD7et7QA3"
              defaultMessage="+{nights, plural, one {# night} other {# nights}} added"
              description="Mobile rewards notification showing nights delta only"
              values={{ nights: nightsDelta }}
            />
          )}
        </BodyText>

        <TextLink size="small" weight="regular" to={`/${LOYALTY_LANDING_PAGE}`} onClick={onViewMembership}>
          {viewMembershipLabel}
        </TextLink>
      </ContentSection>

      <CloseButtonWrapper>
        <IconButton
          kind="tertiary"
          size="small"
          onClick={onClose}
          aria-label={closeAriaLabel}
        >
          <LineTimesIcon colour="normal-contrast" />
        </IconButton>
      </CloseButtonWrapper>
    </MobileNotificationCard>
  )
}

export default LoyaltyRewardsNotificationMobile
